import { useCallback, useEffect, useRef } from 'react';

const getHeightStops = () => {
  if (typeof window === 'undefined') {
    return { collapsed: 80, half: 400, full: 680 };
  }
  return {
    collapsed: 80,
    half: Math.min(window.innerHeight * 0.5, 400),
    full: Math.min(window.innerHeight * 0.85, 680),
  };
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export function useDrawerSnap({ heights = ['collapsed', 'half', 'full'], current, onChange }) {
  const dragStateRef = useRef({
    startY: 0,
    active: false,
  });

  const updateHeightFromY = useCallback(
    (clientY) => {
      const stops = getHeightStops();
      const minHeight = Math.min(...heights.map((key) => stops[key]));
      const maxHeight = Math.max(...heights.map((key) => stops[key]));
      const nextHeight = clamp(window.innerHeight - clientY, minHeight, maxHeight);

      const nearest = heights.reduce(
        (closest, key) => {
          const distance = Math.abs(stops[key] - nextHeight);
          if (distance < closest.distance) {
            return { key, distance };
          }
          return closest;
        },
        { key: current, distance: Number.POSITIVE_INFINITY }
      );

      if (nearest.key && nearest.key !== current && typeof onChange === 'function') {
        onChange(nearest.key);
      }
    },
    [current, heights, onChange]
  );

  const handleMove = useCallback(
    (event) => {
      if (!dragStateRef.current.active) {
        return;
      }
      if (!window?.innerHeight) {
        return;
      }

      const clientY = 'touches' in event ? event.touches[0]?.clientY : event.clientY;
      if (typeof clientY === 'number') {
        updateHeightFromY(clientY);
      }
    },
    [updateHeightFromY]
  );

  const stopDrag = useCallback(() => {
    dragStateRef.current.active = false;
  }, []);

  const handleDragStart = useCallback(
    (event) => {
      dragStateRef.current.active = true;
      const clientY = 'touches' in event ? event.touches[0]?.clientY : event.clientY;
      dragStateRef.current.startY = clientY || 0;
    },
    []
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('touchmove', handleMove, { passive: true });
    window.addEventListener('mouseup', stopDrag);
    window.addEventListener('touchend', stopDrag);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('mouseup', stopDrag);
      window.removeEventListener('touchend', stopDrag);
    };
  }, [handleMove, stopDrag]);

  return {
    handleDragStart,
  };
}
